import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Alert,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar
} from '@mui/material';
import {
  Add,
  Business,
  Assignment,
  People,
  Visibility,
  Edit,
  Delete,
  TrendingUp,
  Euro,
  CheckCircle,
  Schedule,
  Email
} from '@mui/icons-material';
import { createMission } from '../redux/slices/missionsSlice';
import { fetchMyProfile } from '../redux/slices/userProfileSlice';
import organizationService from '../utils/organizationService';
import missionService from '../utils/missionService';
import applicationService from '../utils/applicationService';
import authService from '../utils/authService';
import CityAutocomplete from '../components/CityAutocomplete';
import './DashboardOrg.css';

// Composant pour les statistiques
const StatsCard = ({ icon: Icon, title, value, color, subtitle, loading = false }) => (
  <Card sx={{ height: '100%', borderRadius: 2 }} className="stats-card">
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          {loading ? (
            <>
              <Skeleton variant="text" width={60} height={40} />
              <Skeleton variant="text" width={100} height={20} />
            </>
          ) : (
            <>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#fff' }}>
                {value}
              </Typography>
              <Typography variant="body2" sx={{ color: '#fff' }}>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                  {subtitle}
                </Typography>
              )}
            </>
          )}
        </Box>
        {loading ? (
          <Skeleton variant="circular" width={40} height={40} />
        ) : (
          <Icon sx={{ fontSize: 40, color, opacity: 0.8 }} />
        )}
      </Box>
    </CardContent>
  </Card>
);

// Composant pour les missions
const MissionCard = ({ mission, onEdit, onDelete, applicationCount }) => {
  const coverUrl = mission?.images?.[0]?.url || mission?.media?.[0]?.url || null;
  const city = mission.location?.address?.city || mission.city || '';
  const postalCode = mission.location?.address?.postalCode || mission.postalCode || '';
  const amount = mission.compensation?.amount ? `${mission.compensation.amount}€` : '';
  const status = mission.status || 'published';
  const discipline = mission.discipline || mission.sport || '';
  const applicationsCount = typeof applicationCount === 'number'
    ? applicationCount
    : (Array.isArray(mission.applications) ? mission.applications.length : (mission.stats?.applications || 0));

  return (
    <Card sx={{ mb: 2, borderRadius: 2, backgroundColor: '#fff' }} className="mission-card">
      <CardContent>
        <Box display="flex" gap={2}>
          {coverUrl && (
            <Box sx={{ width: 140, height: 100, borderRadius: 1, overflow: 'hidden', flexShrink: 0 }}>
              <img src={coverUrl} alt={mission.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </Box>
          )}
          <Box flex={1}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
              <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                {mission.title}
              </Typography>
              <Box display="flex" gap={1}>
                <IconButton size="small" onClick={() => onEdit(mission)}>
                  <Edit />
                </IconButton>
                <IconButton size="small" onClick={() => onDelete(mission._id)}>
                  <Delete />
                </IconButton>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {mission.description?.length > 140 ? `${mission.description.substring(0, 140)}...` : (mission.description || '')}
            </Typography>
            <Box display="flex" gap={1} mb={2}>
              <Chip 
                label={status} 
                size="small" 
                color={['published','open','in_progress'].includes(status) ? 'success' : status === 'completed' ? 'primary' : 'default'}
              />
              {discipline && (
                <Chip 
                  label={discipline} 
                  size="small" 
                  variant="outlined"
                />
              )}
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {amount}
                {amount && (city || postalCode) ? ' • ' : ''}
                {city || postalCode || ''}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <People sx={{ fontSize: 16 }} />
                <Typography variant="caption">
                  {applicationsCount} candidatures
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const DashboardOrg = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const profileFetched = useRef(false);
  
  const { user, isAuthenticated } = useSelector(state => state.auth);
  const { profile, loading } = useSelector(state => state.userProfile);
  const profileLoading = loading.profile;
  
  const [tabValue, setTabValue] = useState(0);
  const [openMissionDialog, setOpenMissionDialog] = useState(false);
  const [editingMission, setEditingMission] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // État du formulaire de mission
  const [missionForm, setMissionForm] = useState({
    title: '',
    description: '',
    discipline: '',
    location: '',
    city: '',
    postalCode: '',
    latitude: '',
    longitude: '',
    budget: '',
    duration: '',
    requirements: '',
    startDate: '',
    endDate: '',
    maxParticipants: '1',
    minParticipants: '1',
    category: '',
    level: '',
    startTime: '',
    endTime: '',
    compensationType: 'volunteer',
    compensationAmount: '',
    experienceRequired: 'aucune',
    image: null
  });

  // États pour les données réelles de l'API
  const [stats, setStats] = useState({
    totalMissions: 0,
    activeMissions: 0,
    totalApplicants: 0,
    completedMissions: 0,
    totalMembers: 0,
    totalViews: 0
  });

  const [missions, setMissions] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingMissions, setLoadingMissions] = useState(true);
  const [applications, setApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(true);
  const applicationCountByMission = useMemo(() => {
    const map = {};
    (applications || []).forEach(app => {
      const mid = app?.mission?._id || app?.mission?.id || app?.mission;
      if (!mid) return;
      map[mid] = (map[mid] || 0) + 1;
    });
    return map;
  }, [applications]);

  useEffect(() => {
    console.log('=== DASHBOARD ORG USEEFFECT ===');
    console.log('isAuthenticated:', isAuthenticated);
    console.log('profileLoading:', profileLoading);
    console.log('profileFetched.current:', profileFetched.current);
    console.log('user from Redux:', user);
    console.log('localStorage user:', localStorage.getItem('user'));
    
    // Éviter les appels redondants : ne charger le profil que si l'utilisateur est authentifié,
    // que le profil n'est pas déjà en cours de chargement et qu'il n'a pas déjà été chargé
    if (isAuthenticated && !profileLoading && !profileFetched.current) {
      profileFetched.current = true;
      console.log('Dispatching fetchMyProfile...');
      dispatch(fetchMyProfile());
    }
  }, [dispatch, isAuthenticated, profileLoading]);

  // Vérifier si l'utilisateur est une organisation
  useEffect(() => {
    if (user && user.role !== 'organization') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Résoudre l'ID d'organisation depuis user, profile ou localStorage
  const resolveOrganizationId = () => {
    const stored = (() => {
      try {
        return JSON.parse(localStorage.getItem('user')) || null;
      } catch {
        return null;
      }
    })();
    const rawUserOrg = user?.organization;
    const rawProfileOrg = profile?.organization || profile?.data?.organization;
    const rawStoredOrg = stored?.organization;
    const id = 
      (rawUserOrg && (rawUserOrg._id || rawUserOrg.id || (typeof rawUserOrg === 'string' ? rawUserOrg : null))) ||
      (rawProfileOrg && (rawProfileOrg._id || rawProfileOrg.id || (typeof rawProfileOrg === 'string' ? rawProfileOrg : null))) ||
      (rawStoredOrg && (rawStoredOrg._id || rawStoredOrg.id || (typeof rawStoredOrg === 'string' ? rawStoredOrg : null)));
    console.log('resolveOrganizationId:', { rawUserOrg, rawProfileOrg, rawStoredOrg, resolvedId: id });
    return id || null;
  };

  // Charger les statistiques de l'organisation
  const loadOrganizationStats = async (organizationIdParam) => {
    try {
      setLoadingStats(true);
      const organizationId = organizationIdParam || resolveOrganizationId();
      console.log('=== DEBUG ORGANIZATION STATS ===');
      console.log('User:', user);
      console.log('User role:', user?.role);
      console.log('User organization:', user?.organization);
      console.log('Organization ID:', organizationId);
      
      const isOrganizationRole = (user?.role === 'organization') || (profile?.role === 'organization') || (() => {
        try { return (JSON.parse(localStorage.getItem('user')) || {}).role === 'organization'; } catch { return false; }
      })();

      if (organizationId && isOrganizationRole) {
        console.log('Appel API getOrganizationStatsById avec ID:', organizationId);
        const response = await organizationService.getOrganizationStatsById(organizationId);
        console.log('Réponse API stats:', response);
        if (response.success) {
          const statsData = response.data;
          setStats({
            totalMissions: statsData.overview?.totalMissions || 0,
            activeMissions: statsData.overview?.activeMissions || 0,
            completedMissions: statsData.overview?.completedMissions || 0,
            totalMembers: statsData.overview?.totalMembers || 0,
            totalViews: statsData.overview?.totalViews || 0,
            totalApplicants: stats.totalApplicants || 0,
            missionsByStatus: statsData.missionsByStatus || {},
            recentMissions: statsData.recentMissions || []
          });
        } else {
          const byStatus = missions.reduce((acc, m) => {
            const s = m.status || 'draft';
            acc[s] = (acc[s] || 0) + 1;
            return acc;
          }, {});
          const recent = missions
            .slice()
            .sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0,5)
            .map(m => ({ _id: m._id, title: m.title, status: m.status }));
          setStats(prev => ({
            ...prev,
            missionsByStatus: byStatus,
            recentMissions: recent
          }));
        }
      } else {
        console.log('Conditions non remplies pour charger les stats:', {
          organizationId,
          userRole: user?.role,
          profileRole: profile?.role,
          hasOrganizationUser: !!user?.organization,
          hasOrganizationProfile: !!profile?.organization
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors du chargement des statistiques',
        severity: 'error'
      });
    } finally {
      setLoadingStats(false);
    }
  };

  // Charger les missions de l'organisation
  const loadOrganizationMissions = async () => {
    try {
      setLoadingMissions(true);
      const organizationId = resolveOrganizationId();
      console.log('loadOrganizationMissions organizationId:', organizationId);
      if (!organizationId) {
        console.log('Organization ID introuvable, utilisation fallback missions utilisateur');
        const userResp = await missionService.getUserMissions();
        console.log('Réponse API missions utilisateur:', userResp);
        const data = userResp.success ? (userResp.data || []) : [];
        setMissions(data);
        const totalApplicants = data.reduce((total, mission) => {
          const count = Array.isArray(mission.applications) ? mission.applications.length : mission.stats?.applications || 0;
          return total + count;
        }, 0);
        const totalMissions = Array.isArray(data) ? data.length : 0;
        const activeMissions = Array.isArray(data) ? data.filter(m => ['published', 'open', 'in_progress'].includes(m.status)).length : 0;
        const completedMissions = Array.isArray(data) ? data.filter(m => m.status === 'completed').length : 0;
        setStats(prev => ({ 
          ...prev, 
          totalApplicants,
          totalMissions,
          activeMissions,
          completedMissions
        }));
        return;
      }
      const response = await organizationService.getOrganizationMissions(organizationId);
      console.log('Réponse API missions organisation:', response);
      let missionsData = response.success ? (response.data || []) : [];
      if (!Array.isArray(missionsData) || missionsData.length === 0) {
        console.log('Aucune mission organisation renvoyée, fallback vers missions utilisateur');
        const userResp = await missionService.getUserMissions();
        console.log('Réponse API missions utilisateur (fallback):', userResp);
        if (userResp.success) {
          missionsData = userResp.data || [];
        }
      }
      setMissions(missionsData);
      let totalApplicants = 0;
      try {
        const appsResp = await applicationService.getApplicationsForMyMissions();
        if (appsResp && appsResp.success) {
          totalApplicants = typeof appsResp.total === 'number' ? appsResp.total : (Array.isArray(appsResp.data) ? appsResp.data.length : 0);
          setApplications(Array.isArray(appsResp.data) ? appsResp.data : []);
          setLoadingApplications(false);
        } else {
          totalApplicants = missionsData.reduce((total, mission) => {
            const count = Array.isArray(mission.applications) ? mission.applications.length : mission.stats?.applications || 0;
            return total + count;
          }, 0);
          setApplications([]);
          setLoadingApplications(false);
        }
      } catch (e) {
        totalApplicants = missionsData.reduce((total, mission) => {
          const count = Array.isArray(mission.applications) ? mission.applications.length : mission.stats?.applications || 0;
          return total + count;
        }, 0);
        setApplications([]);
        setLoadingApplications(false);
      }
      console.log('Missions chargées:', { count: missionsData.length, totalApplicants });
      const totalMissions = Array.isArray(missionsData) ? missionsData.length : 0;
      const activeMissions = Array.isArray(missionsData) ? missionsData.filter(m => ['published', 'open', 'in_progress'].includes(m.status)).length : 0;
      const completedMissions = Array.isArray(missionsData) ? missionsData.filter(m => m.status === 'completed').length : 0;
      setStats(prev => ({
        ...prev,
        totalApplicants,
        totalMissions,
        activeMissions,
        completedMissions
      }));
      const orgId = resolveOrganizationId();
      if (orgId) {
        try { await loadOrganizationStats(orgId); } catch {}
      }
    } catch (error) {
      console.error('Erreur lors du chargement des missions:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors du chargement des missions',
        severity: 'error'
      });
    } finally {
      setLoadingMissions(false);
    }
  };

  const loadApplicationsForMyMissions = async () => {
    try {
      setLoadingApplications(true);
      const appsResp = await applicationService.getApplicationsForMyMissions();
      if (appsResp && appsResp.success) {
        const apps = Array.isArray(appsResp.data) ? appsResp.data : [];
        setApplications(apps);
        const applicantsCount = typeof appsResp.total === 'number' ? appsResp.total : apps.length;
        setStats(prev => ({ ...prev, totalApplicants: applicantsCount }));
      } else {
        setApplications([]);
        setStats(prev => ({ ...prev, totalApplicants: 0 }));
      }
    } catch (error) {
      console.error('Erreur lors du chargement des candidatures:', error);
      setApplications([]);
      setStats(prev => ({ ...prev, totalApplicants: 0 }));
    } finally {
      setLoadingApplications(false);
    }
  };

  // Charger les données quand l'utilisateur ou le profil est disponible
  useEffect(() => {
    const organizationId = resolveOrganizationId();
    const isOrganizationRole = (user?.role === 'organization') || (profile?.role === 'organization') || (() => {
      try { return (JSON.parse(localStorage.getItem('user')) || {}).role === 'organization'; } catch { return false; }
    })();
    if (isOrganizationRole) {
      if (organizationId) {
        loadOrganizationStats(organizationId);
      } else {
        setLoadingStats(false);
      }
      loadOrganizationMissions();
      loadApplicationsForMyMissions();
    } else {
      setLoadingStats(false);
      setLoadingMissions(false);
      setLoadingApplications(false);
    }
  }, [user, profile]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenMissionDialog = (mission = null) => {
    if (mission) {
      setEditingMission(mission);
      setMissionForm({
        title: mission.title,
        description: mission.description,
        category: mission.category || 'coaching',
        sport: mission.sport || mission.discipline || '',
        level: mission.level || 'debutant',
        targetAudience: mission.targetAudience || [],
        discipline: mission.discipline,
        location: mission.location?.address?.street || mission.location || '',
        city: mission.location?.address?.city || mission.city || '',
        postalCode: mission.location?.address?.postalCode || mission.postalCode || '',
        latitude: mission.location?.coordinates?.[1] || '', // latitude est à l'index 1
        longitude: mission.location?.coordinates?.[0] || '', // longitude est à l'index 0
        budget: mission.budget?.toString() || '',
        duration: mission.duration || '',
        requirements: mission.requirements || '',
        startDate: mission.startDate || '',
        endDate: mission.endDate || '',
        startTime: mission.schedule?.startTime || '',
        endTime: mission.schedule?.endTime || '',
        maxParticipants: mission.participants?.max || 1,
        minParticipants: mission.participants?.required || 1,
        compensationType: mission.compensation?.type || 'volunteer',
        compensationAmount: mission.compensation?.amount || '',
        image: null
      });
    } else {
      setEditingMission(null);
      setMissionForm({
        title: '',
        description: '',
        category: 'coaching',
        sport: '',
        level: 'debutant',
        targetAudience: [],
        discipline: '',
        location: '',
        city: '',
        postalCode: '',
        latitude: '',
        longitude: '',
        budget: '',
        duration: '',
        requirements: '',
        startDate: '',
        endDate: '',
        startTime: '',
        endTime: '',
        maxParticipants: 1,
        minParticipants: 1,
        compensationType: 'volunteer',
        compensationAmount: '',
        experienceRequired: 'aucune',
        image: null
      });
    }
    setOpenMissionDialog(true);
  };

  const handleCloseMissionDialog = () => {
    setOpenMissionDialog(false);
    setEditingMission(null);
  };

  const handleMissionFormChange = (field, value) => {
    setMissionForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCoordinatesChange = (coordinates) => {
    setMissionForm(prev => ({
      ...prev,
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
      postalCode: coordinates.postcode || prev.postalCode
    }));
  };

  const handleSubmitMission = async () => {
    try {
      // Debug: Afficher les valeurs du formulaire
      console.log('Valeurs du formulaire:', missionForm);
      
      // Validation des champs requis
      if (!missionForm.title?.trim()) {
        throw new Error('Le titre de la mission est requis');
      }
      if (!missionForm.description?.trim()) {
        throw new Error('La description de la mission est requise');
      }
      if (!missionForm.category) {
        throw new Error('La catégorie est requise');
      }
      if (!missionForm.level) {
        throw new Error('Le niveau est requis');
      }
      if (!missionForm.startDate) {
        throw new Error('La date de début est requise');
      }
      if (!missionForm.endDate) {
        throw new Error('La date de fin est requise');
      }
      if (!missionForm.startTime) {
        throw new Error('L\'heure de début est requise');
      }
      if (!missionForm.endTime) {
        throw new Error('L\'heure de fin est requise');
      }
      
      // S'assurer que tous les champs requis ont des valeurs valides
      const street = missionForm.location?.trim() || '';
      const city = missionForm.city?.trim() || '';
      const postalCode = missionForm.postalCode?.trim() || '';
      const latitude = parseFloat(missionForm.latitude);
      const longitude = parseFloat(missionForm.longitude);
      
      if (!street || !city || !postalCode) {
        throw new Error('Veuillez remplir tous les champs d\'adresse obligatoires');
      }
      
      if (!missionForm.latitude || !missionForm.longitude || isNaN(latitude) || isNaN(longitude)) {
        throw new Error('Veuillez sélectionner une ville pour obtenir les coordonnées GPS');
      }
      
      if (!missionForm.maxParticipants || parseInt(missionForm.maxParticipants) < 1) {
        throw new Error('Le nombre maximum de participants doit être d\'au moins 1');
      }
      
      console.log('Valeurs après traitement:', { street, city, postalCode, latitude, longitude });
      
      const mapCategory = (cat) => {
        switch ((cat || '').toLowerCase()) {
          case 'coaching': return 'coaching';
          case 'arbitrage': return 'referee';
          case 'animation': return 'event_organization';
          case 'entrainement': return 'training';
          case 'evenement': return 'event_organization';
          case 'competition': return 'other';
          case 'autre': return 'other';
          default: return 'other';
        }
      };
      const mapExperience = (exp) => {
        switch ((exp || '').toLowerCase()) {
          case 'aucune': return 'none';
          case 'debutant': return 'beginner';
          case 'intermediaire': return 'intermediate';
          case 'avance': return 'advanced';
          case 'expert': return 'expert';
          default: return undefined;
        }
      };
      const now = new Date();
      let startDateObj = new Date(missionForm.startDate);
      if (!(startDateObj instanceof Date) || isNaN(startDateObj)) startDateObj = now;
      if (startDateObj <= now) {
        startDateObj = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      }
      let endDateObj = new Date(missionForm.endDate);
      if (!(endDateObj instanceof Date) || isNaN(endDateObj) || endDateObj <= startDateObj) {
        endDateObj = new Date(startDateObj.getTime() + 24 * 60 * 60 * 1000);
      }

      const missionData = {
        title: missionForm.title,
        description: missionForm.description,
        category: mapCategory(missionForm.category),
        sport: missionForm.discipline?.toLowerCase() || missionForm.sport?.toLowerCase() || 'football',
        level: missionForm.level,
        targetAudience: missionForm.targetAudience?.length > 0 ? missionForm.targetAudience : ['tous'],
        location: {
          type: 'Point',
          coordinates: [longitude, latitude],
          address: {
            street: street,
            city: city,
            postalCode: postalCode,
            country: 'France'
          }
        },
        schedule: {
          startDate: startDateObj.toISOString(),
          endDate: endDateObj.toISOString(),
          startTime: missionForm.startTime,
          endTime: missionForm.endTime,
          isRecurring: false
        },
        participants: {
          max: parseInt(missionForm.maxParticipants) || 1,
          required: parseInt(missionForm.minParticipants) || 1,
          current: 0
        },
        compensation: {
          type: missionForm.compensationType === 'paid' ? 'paid' : 'volunteer',
          amount: missionForm.compensationType === 'paid' ? parseFloat(missionForm.compensationAmount) || 0 : 0,
          currency: 'EUR'
        },
        requirements: {
          experience: mapExperience(missionForm.experienceRequired),
          equipment: [],
          certifications: []
        },
        status: 'published',
        visibility: 'public',
        tags: [],
        createdBy: user?._id || user?.id || profile?._id || null
      };

      console.log('Données à envoyer:', missionData);

      let result;
      if (editingMission) {
        result = await missionService.updateMission(editingMission._id, missionData);
      } else {
        result = await missionService.createMission(missionData);
      }

      console.log('Résultat:', result);

      if (result.success) {
        try {
          const createdMissionId = result.data?._id || result.data?.id;
          if (createdMissionId && missionForm.image) {
            const formData = new FormData();
            formData.append('media', missionForm.image);
            const uploadResp = await missionService.uploadMissionMedia(createdMissionId, formData);
            console.log('Upload image mission response:', uploadResp);
          }
        } catch (e) {
          console.error('Erreur upload image mission:', e);
        }
        setSnackbar({
          open: true,
          message: editingMission ? 'Mission modifiée avec succès!' : 'Mission créée avec succès!',
          severity: 'success'
        });
        handleCloseMissionDialog();
        loadOrganizationMissions();
        const orgIdAfterCreate = resolveOrganizationId();
        if (orgIdAfterCreate) {
          loadOrganizationStats(orgIdAfterCreate);
        }
      } else {
        throw new Error(result.message || 'Erreur lors de la création de la mission');
      }
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Erreur lors de la création de la mission',
        severity: 'error'
      });
    }
  };

  const handleDeleteMission = async (missionId) => {
    try {
      await missionService.deleteMission(missionId);
      setSnackbar({
        open: true,
        message: 'Mission supprimée avec succès',
        severity: 'success'
      });
      
      // Recharger les données
      loadOrganizationMissions();
      loadOrganizationStats();
    } catch (error) {
      console.error('Erreur lors de la suppression de la mission:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la suppression de la mission',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleResendEmail = async () => {
    try {
      await authService.resendEmailVerification();
      setSnackbar({
        open: true,
        message: 'Email de vérification renvoyé avec succès',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Erreur lors de l\'envoi de l\'email',
        severity: 'error'
      });
    }
  };

  if (user && user.isEmailVerified === false) {
    return (
      <Container maxWidth="sm" sx={{ py: 10, textAlign: 'center' }}>
        <Paper sx={{ p: 4, borderRadius: 2 }}>
          <Avatar sx={{ width: 64, height: 64, mx: 'auto', mb: 2, bgcolor: 'warning.main' }}>
            <Email sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography variant="h5" gutterBottom>
            Vérification requise
          </Typography>
          <Typography color="text.secondary" paragraph>
            Votre compte a été créé avec succès, mais vous devez vérifier votre adresse email avant d'accéder au tableau de bord.
          </Typography>
          <Typography variant="body2" paragraph>
            Un email a été envoyé à <strong>{user.email}</strong>.
          </Typography>
          <Button 
            variant="contained" 
            onClick={handleResendEmail}
            sx={{ mt: 2 }}
          >
            Renvoyer l'email de vérification
          </Button>
          <Box mt={2}>
             <Button variant="text" onClick={() => authService.logout().then(() => navigate('/login'))}>
               Se déconnecter
             </Button>
          </Box>
        </Paper>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    );
  }

  if (profileLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map(i => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  const organizationName = profile?.organization?.name || user?.organization?.name || 'Mon Organisation';

  return (
    <>
      <Helmet>
        <title>Tableau de bord Organisation - Mission Sport</title>
        <meta name="description" content="Gérez vos missions et recrutements sur votre tableau de bord organisation Mission Sport." />
      </Helmet>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, backgroundColor: '#fff' }} className="dashboard-header">
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
            <Box display="flex" alignItems="center">
              <Avatar sx={{ width: 64, height: 64, mr: 2, bgcolor: 'primary.main' }}>
                <Business sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" component="h1" gutterBottom>
                  {organizationName}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Tableau de bord organisation
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenMissionDialog()}
              size="large"
            >
              Publier une mission
            </Button>
          </Box>
        </Paper>

        {/* Statistiques */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              icon={Assignment}
              title="Missions totales"
              value={stats.totalMissions}
              color="#2196f3"
              loading={loadingStats}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              icon={CheckCircle}
              title="Missions actives"
              value={stats.activeMissions}
              color="#4caf50"
              loading={loadingStats}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              icon={People}
              title="Candidatures reçues"
              value={stats.totalApplicants}
              color="#ff9800"
              loading={loadingStats}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              icon={TrendingUp}
              title="Missions terminées"
              value={stats.completedMissions}
              color="#9c27b0"
              loading={loadingStats}
            />
          </Grid>
        </Grid>

        {/* Contenu principal */}
        <Paper sx={{ borderRadius: 2, backgroundColor: '#fff' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Mes missions" />
              <Tab label="Candidatures" />
              <Tab label="Statistiques" />
            </Tabs>
          </Box>

          <Box sx={{ p: 3 }}>
            {tabValue === 0 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h6">
                    Mes missions publiées
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() => handleOpenMissionDialog()}
                  >
                    Nouvelle mission
                  </Button>
                </Box>
                
                {loadingMissions ? (
                  // Affichage des skeletons pendant le chargement
                  Array.from({ length: 3 }).map((_, index) => (
                    <Card key={index} sx={{ mb: 2, borderRadius: 2 }}>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                          <Box flex={1}>
                            <Skeleton variant="text" width="60%" height={32} />
                            <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
                            <Box display="flex" gap={1} mb={2}>
                              <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} />
                              <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
                            </Box>
                          </Box>
                          <Box display="flex" gap={1}>
                            <Skeleton variant="circular" width={32} height={32} />
                            <Skeleton variant="circular" width={32} height={32} />
                          </Box>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Skeleton variant="text" width="40%" height={20} />
                          <Skeleton variant="text" width="30%" height={20} />
                        </Box>
                      </CardContent>
                    </Card>
                  ))
                ) : missions.length > 0 ? (
                  missions.map(mission => (
                    <MissionCard
                      key={mission._id}
                      mission={mission}
                      applicationCount={applicationCountByMission[mission._id]}
                      onEdit={handleOpenMissionDialog}
                      onDelete={handleDeleteMission}
                    />
                  ))
                ) : (
                  <Alert severity="info">
                    Aucune mission publiée pour le moment. Cliquez sur "Nouvelle mission" pour commencer.
                  </Alert>
                )}
              </Box>
            )}

            {tabValue === 1 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h6">
                    Candidatures reçues
                  </Typography>
                </Box>

                {loadingApplications ? (
                  Array.from({ length: 3 }).map((_, index) => (
                    <Card key={index} sx={{ mb: 2, borderRadius: 2 }}>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                          <Box flex={1}>
                            <Skeleton variant="text" width="50%" height={28} />
                            <Skeleton variant="text" width="70%" height={20} />
                          </Box>
                          <Skeleton variant="rectangular" width={100} height={24} sx={{ borderRadius: 1 }} />
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Skeleton variant="text" width="30%" height={20} />
                          <Skeleton variant="text" width="20%" height={20} />
                        </Box>
                      </CardContent>
                    </Card>
                  ))
                ) : applications.length > 0 ? (
                  applications.map(app => {
                    const avatarUrl = app?.applicant?.avatar?.url || '/default-avatar.svg';
                    return (
                      <Card key={app._id} sx={{ mb: 2, borderRadius: 2, backgroundColor: '#fff' }}>
                        <CardContent>
                          <Box display="flex" gap={2}>
                            <Box sx={{ width: 64, height: 64, borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
                              <img src={avatarUrl} alt={`${app.applicant?.firstName || ''} ${app.applicant?.lastName || ''}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </Box>
                            <Box flex={1}>
                              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                <Typography variant="subtitle1" gutterBottom sx={{ mb: 0 }}>
                                  {app.mission?.title || 'Mission'}
                                </Typography>
                                <Chip 
                                  label={app.status || 'pending'} 
                                  size="small" 
                                  color={app.status === 'accepted' ? 'success' : app.status === 'rejected' ? 'error' : 'default'}
                                />
                              </Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Candidat: {app.applicant?.firstName} {app.applicant?.lastName}
                              </Typography>
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(app.createdAt).toLocaleDateString()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {app.mission?.schedule?.startDate ? `Début: ${new Date(app.mission.schedule.startDate).toLocaleDateString()}` : ''}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })
                ) : (
                  <Alert severity="info">
                    Aucune candidature reçue pour le moment.
                  </Alert>
                )}
              </Box>
            )}

            {tabValue === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Statistiques détaillées
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Répartition des missions
                      </Typography>
                      {stats.missionsByStatus ? (
                        Object.entries(stats.missionsByStatus).map(([status, count]) => (
                          <Box key={status} display="flex" justifyContent="space-between" my={0.5}>
                            <Typography variant="body2">{status}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{count}</Typography>
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">Aucune donnée disponible</Typography>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Missions récentes
                      </Typography>
                      {Array.isArray(stats.recentMissions) && stats.recentMissions.length > 0 ? (
                        stats.recentMissions.map((m) => (
                          <Box key={m._id} display="flex" justifyContent="space-between" my={0.5}>
                            <Typography variant="body2">{m.title}</Typography>
                            <Chip label={m.status} size="small" />
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">Aucune mission récente</Typography>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Dialog de création/modification de mission */}
        <Dialog 
          open={openMissionDialog} 
          onClose={handleCloseMissionDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingMission ? 'Modifier la mission' : 'Publier une nouvelle mission'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Titre de la mission"
                  value={missionForm.title}
                  onChange={(e) => handleMissionFormChange('title', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={4}
                  value={missionForm.description}
                  onChange={(e) => handleMissionFormChange('description', e.target.value)}
                  required
                />
               </Grid>
               <Grid item xs={12}>
                 <Box>
                   <Typography variant="body2" color="text.secondary" gutterBottom>
                     Image de la mission (optionnel)
                   </Typography>
                   <Button
                     variant="outlined"
                     component="label"
                     fullWidth
                     sx={{ 
                       height: 56, 
                       borderStyle: 'dashed',
                       '&:hover': { borderStyle: 'dashed' }
                     }}
                   >
                     {missionForm.image ? missionForm.image.name : 'Choisir une image'}
                     <input
                       type="file"
                       hidden
                       accept="image/*"
                       onChange={(e) => {
                         const file = e.target.files[0];
                         handleMissionFormChange('image', file);
                       }}
                     />
                   </Button>
                   {missionForm.image && (
                     <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                       Fichier sélectionné: {missionForm.image.name}
                     </Typography>
                   )}
                 </Box>
               </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Discipline</InputLabel>
                  <Select
                    value={missionForm.discipline}
                    onChange={(e) => handleMissionFormChange('discipline', e.target.value)}
                    label="Discipline"
                  >
                    <MenuItem value="Football">Football</MenuItem>
                    <MenuItem value="Basketball">Basketball</MenuItem>
                    <MenuItem value="Tennis">Tennis</MenuItem>
                    <MenuItem value="Natation">Natation</MenuItem>
                    <MenuItem value="Athlétisme">Athlétisme</MenuItem>
                    <MenuItem value="Préparation physique">Préparation physique</MenuItem>
                    <MenuItem value="Autre">Autre</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Catégorie</InputLabel>
                  <Select
                    value={missionForm.category}
                    onChange={(e) => handleMissionFormChange('category', e.target.value)}
                    label="Catégorie"
                  >
                    <MenuItem value="coaching">Coaching</MenuItem>
                    <MenuItem value="arbitrage">Arbitrage</MenuItem>
                    <MenuItem value="animation">Animation</MenuItem>
                    <MenuItem value="entrainement">Entraînement</MenuItem>
                    <MenuItem value="competition">Compétition</MenuItem>
                    <MenuItem value="evenement">Événement</MenuItem>
                    <MenuItem value="autre">Autre</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Niveau requis</InputLabel>
                  <Select
                    value={missionForm.level}
                    onChange={(e) => handleMissionFormChange('level', e.target.value)}
                    label="Niveau requis"
                  >
                    <MenuItem value="debutant">Débutant</MenuItem>
                    <MenuItem value="intermediaire">Intermédiaire</MenuItem>
                    <MenuItem value="avance">Avancé</MenuItem>
                    <MenuItem value="expert">Expert</MenuItem>
                    <MenuItem value="tous_niveaux">Tous niveaux</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Public cible</InputLabel>
                  <Select
                    multiple
                    value={missionForm.targetAudience}
                    onChange={(e) => handleMissionFormChange('targetAudience', e.target.value)}
                    label="Public cible"
                  >
                    <MenuItem value="enfants">Enfants</MenuItem>
                    <MenuItem value="adolescents">Adolescents</MenuItem>
                    <MenuItem value="adultes">Adultes</MenuItem>
                    <MenuItem value="seniors">Seniors</MenuItem>
                    <MenuItem value="tous">Tous</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Adresse"
                  value={missionForm.location}
                  onChange={(e) => handleMissionFormChange('location', e.target.value)}
                  placeholder="Ex: 123 Rue de la République"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <CityAutocomplete
                  value={missionForm.city}
                  onChange={(value) => handleMissionFormChange('city', value)}
                  onCoordinatesChange={handleCoordinatesChange}
                  label="Ville"
                  placeholder="Tapez le nom d'une ville..."
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Code postal"
                  value={missionForm.postalCode}
                  onChange={(e) => handleMissionFormChange('postalCode', e.target.value)}
                  placeholder="Ex: 75001"
                  inputProps={{ maxLength: 5, pattern: '[0-9]{5}' }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Latitude"
                  type="number"
                  value={missionForm.latitude}
                  placeholder="Sélectionnez une ville pour remplir automatiquement"
                  inputProps={{ step: "any", readOnly: true }}
                  helperText="Rempli automatiquement lors de la sélection de la ville"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Longitude"
                  type="number"
                  value={missionForm.longitude}
                  placeholder="Sélectionnez une ville pour remplir automatiquement"
                  inputProps={{ step: "any", readOnly: true }}
                  helperText="Rempli automatiquement lors de la sélection de la ville"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Budget (€)"
                  type="number"
                  value={missionForm.budget}
                  onChange={(e) => handleMissionFormChange('budget', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Durée"
                  value={missionForm.duration}
                  onChange={(e) => handleMissionFormChange('duration', e.target.value)}
                  placeholder="Ex: 3 mois, 1 an..."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date de début"
                  type="date"
                  value={missionForm.startDate}
                  onChange={(e) => handleMissionFormChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date de fin"
                  type="date"
                  value={missionForm.endDate}
                  onChange={(e) => handleMissionFormChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Heure de début"
                  type="time"
                  value={missionForm.startTime}
                  onChange={(e) => handleMissionFormChange('startTime', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Heure de fin"
                  type="time"
                  value={missionForm.endTime}
                  onChange={(e) => handleMissionFormChange('endTime', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Participants minimum"
                  type="number"
                  value={missionForm.minParticipants}
                  onChange={(e) => handleMissionFormChange('minParticipants', e.target.value)}
                  inputProps={{ min: 1 }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Participants maximum"
                  type="number"
                  value={missionForm.maxParticipants}
                  onChange={(e) => handleMissionFormChange('maxParticipants', e.target.value)}
                  inputProps={{ min: 1 }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Type de compensation</InputLabel>
                  <Select
                    value={missionForm.compensationType}
                    onChange={(e) => handleMissionFormChange('compensationType', e.target.value)}
                    label="Type de compensation"
                  >
                    <MenuItem value="paid">Rémunéré</MenuItem>
                    <MenuItem value="volunteer">Bénévolat</MenuItem>
                    <MenuItem value="exchange">Échange de services</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {missionForm.compensationType === 'paid' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Montant (€)"
                    type="number"
                    value={missionForm.compensationAmount}
                    onChange={(e) => handleMissionFormChange('compensationAmount', e.target.value)}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Niveau d'expérience requis</InputLabel>
                  <Select
                    value={missionForm.experienceRequired}
                    onChange={(e) => handleMissionFormChange('experienceRequired', e.target.value)}
                    label="Niveau d'expérience requis"
                  >
                    <MenuItem value="aucune">Aucune expérience requise</MenuItem>
                    <MenuItem value="debutant">Débutant</MenuItem>
                    <MenuItem value="intermediaire">Intermédiaire</MenuItem>
                    <MenuItem value="avance">Avancé</MenuItem>
                    <MenuItem value="expert">Expert</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Exigences et qualifications"
                  multiline
                  rows={3}
                  value={missionForm.requirements}
                  onChange={(e) => handleMissionFormChange('requirements', e.target.value)}
                  placeholder="Diplômes requis, compétences spécifiques..."
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseMissionDialog}>
              Annuler
            </Button>
            <Button 
              onClick={handleSubmitMission}
              variant="contained"
              disabled={!missionForm.title || !missionForm.description || !missionForm.discipline || !missionForm.location || !missionForm.city || !missionForm.postalCode || !missionForm.category || !missionForm.level || !missionForm.startTime || !missionForm.endTime || !missionForm.maxParticipants || !missionForm.compensationType}
            >
              {editingMission ? 'Modifier' : 'Publier'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default DashboardOrg;